import React, { useEffect, useState } from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiAlertTriangle, FiFilePlus, FiUpload, FiX } from "react-icons/fi";
import { MAX_FILE_SIZE } from "../../../services/client/clientDocumentService";

const categoryOptions = [
  ["general", "General"], ["tax", "Tax"], ["bank_statement", "Bank statement"],
  ["financial_statement", "Financial statement"], ["contract", "Contract"],
  ["invoice", "Invoice"], ["receipt", "Receipt"], ["payroll", "Payroll"],
  ["legal", "Legal"], ["other", "Other"],
];

export default function ClientDocumentUploadModal({
  open,
  organizationId,
  onClose,
  onUpload,
}) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("general");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("shared_with_accountant");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, busy, onClose]);

  useEffect(() => {
    if (open) {
      setFile(null);
      setCategory("general");
      setDescription("");
      setVisibility("shared_with_accountant");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      await onUpload({ organizationId, file, category, description, visibility });
      onClose();
    } catch (uploadError) {
      setError(uploadError?.message || "Unable to upload this document.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={(event) => event.target === event.currentTarget && !busy && onClose()}>
      <section role="dialog" aria-modal="true" aria-labelledby="upload-document-title" className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-xl sm:rounded-3xl">
        <header className="flex items-start justify-between border-b border-slate-100 px-5 py-4 sm:px-7">
          <div><p className="text-xs font-bold uppercase tracking-widest text-violet-700">Private storage</p><h2 id="upload-document-title" className="mt-1 text-xl font-black text-slate-900">Upload document</h2></div>
          <button type="button" aria-label="Close upload dialog" disabled={busy} onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"><SafeIcon icon={FiX} /></button>
        </header>
        <form onSubmit={submit} className="space-y-4 p-5 sm:p-7">
          <p className="text-sm leading-6 text-slate-600">Files are stored privately and shared according to your visibility choice. Never enter bank login credentials in a document.</p>
          <label className="block rounded-2xl border-2 border-dashed border-slate-200 p-5 text-center transition hover:border-violet-300">
            <SafeIcon icon={FiFilePlus} className="mx-auto text-2xl text-violet-600" />
            <span className="mt-2 block text-sm font-bold text-slate-800">{file?.name || "Choose a file"}</span>
            <span className="mt-1 block text-xs text-slate-500">PDF, JPG, PNG, WebP, DOCX, XLSX · Maximum 20 MB</span>
            <input type="file" required accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx,application/pdf,image/jpeg,image/png,image/webp" disabled={busy} onChange={(event) => { setError(""); setFile(event.target.files?.[0] || null); }} className="sr-only" />
          </label>
          {file && file.size > MAX_FILE_SIZE && <p role="alert" className="text-xs font-semibold text-rose-700">This file exceeds the 20 MB limit.</p>}
          <label className="block text-xs font-bold text-slate-600">Category
            <select required value={category} disabled={busy} onChange={(event) => setCategory(event.target.value)} className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800">
              {categoryOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="block text-xs font-bold text-slate-600">Description <span className="font-normal text-slate-400">Optional</span>
            <textarea value={description} disabled={busy} onChange={(event) => setDescription(event.target.value)} rows="3" maxLength="1000" className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-sm font-normal text-slate-800 outline-none focus:border-violet-400" />
          </label>
          <label className="block text-xs font-bold text-slate-600">Visibility
            <select required value={visibility} disabled={busy} onChange={(event) => setVisibility(event.target.value)} className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800">
              <option value="shared_with_accountant">Shared with accountant</option>
              <option value="client_only">Private to this business</option>
            </select>
          </label>
          {error && <p role="alert" className="flex gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"><SafeIcon icon={FiAlertTriangle} className="mt-0.5 shrink-0" />{error}</p>}
          <footer className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" disabled={busy} onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={busy || !organizationId || !file || file.size > MAX_FILE_SIZE} className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"><SafeIcon icon={FiUpload} />{busy ? "Uploading…" : "Upload document"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}